import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const ANNOTATION_SOURCE_ENTITY_TOKEN = new StateToken<AnnotationSourceEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_SOURCE_ENTITY);

export type AnnotationSourceEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.AnnotationSourceEntityModels.AnnotationSource>;

@State<AnnotationSourceEntityStateModel>({
  name: ANNOTATION_SOURCE_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class AnnotationSourceEntityState extends HistoryEntityBaseState<FlatModels.AnnotationSourceEntityModels.AnnotationSource, AnnotationSourceEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([AnnotationSourceEntityState])
  static override typedEntities$(state: AnnotationSourceEntityStateModel): { [id: string]: FlatModels.AnnotationSourceEntityModels.AnnotationSource } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([AnnotationSourceEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.AnnotationSourceEntityModels.AnnotationSource }): FlatModels.AnnotationSourceEntityModels.AnnotationSource[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([AnnotationSourceEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.AnnotationSourceEntityModels.AnnotationSource }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([AnnotationSourceEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.AnnotationSourceEntityModels.AnnotationSource }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.AnnotationSourceEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<AnnotationSourceEntityStateModel>,
                            action: FlatActions.AnnotationSourceEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationSourceEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<AnnotationSourceEntityStateModel>,
                              action: FlatActions.AnnotationSourceEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.AnnotationSourceEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<AnnotationSourceEntityStateModel>,
                               action: FlatActions.AnnotationSourceEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationSourceEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<AnnotationSourceEntityStateModel>,
                                 action: FlatActions.AnnotationSourceEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.AnnotationSourceEntityActions.AddEntity)
  @Undoable(
    FlatActions.AnnotationSourceEntityActions.AddEntity,
    ANNOTATION_SOURCE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<AnnotationSourceEntityStateModel>,
                     action: FlatActions.AnnotationSourceEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.AnnotationSourceEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.AnnotationSourceEntityActions.UpdateEntity,
    ANNOTATION_SOURCE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<AnnotationSourceEntityStateModel>,
                        action: FlatActions.AnnotationSourceEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.AnnotationSourceEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.AnnotationSourceEntityActions.RemoveEntity,
    ANNOTATION_SOURCE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<AnnotationSourceEntityStateModel>,
                        action: FlatActions.AnnotationSourceEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.AnnotationSourceEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<AnnotationSourceEntityStateModel>,
                        action: FlatActions.AnnotationSourceEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationSourceEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<AnnotationSourceEntityStateModel>,
                        action: FlatActions.AnnotationSourceEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationSourceEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<AnnotationSourceEntityStateModel>,
                            action: FlatActions.AnnotationSourceEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  //</editor-fold>
}
